import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useVoice } from '../context/VoiceContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, 
  MicOff, 
  Send, 
  Clock, 
  CheckCircle2, 
  Bell, 
  ListTodo,
  Sparkles,
  Volume2,
  VolumeX,
  Settings,
  HelpCircle,
  Loader2,
  X,
  ChevronRight,
  Newspaper
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import { AIBrainVisualizer } from '../components/ui/AIVoiceVisualizer';
import { VoiceSettingsPanel } from '../components/ui/VoiceSettingsPanel';
import NewsSection from '../components/news/NewsSection';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssistantPage() {
  const { token, user } = useAuth();
  const { theme } = useTheme();
  const { voiceEnabled, speak, stopSpeaking, isSpeaking, onSpeakingChange } = useVoice();
  const [visualizerState, setVisualizerState] = useState('idle');
  const [inputText, setInputText] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [suggestions, setSuggestions] = useState([
    "Plan my day",
    "Set a reminder",
    "Show my tasks",
    "Help me with something"
  ]);
  const [upcomingItems, setUpcomingItems] = useState({ reminders: [], tasks: [] });
  
  const visualizerRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  const conversationEndRef = useRef(null);

  // Initialize Web Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Sync voice speaking state with visualizer
  useEffect(() => {
    const unsubscribe = onSpeakingChange((speaking) => {
      if (speaking) {
        setVisualizerState('speaking');
      } else if (!isProcessing) {
        setVisualizerState('idle');
      }
    });
    return unsubscribe;
  }, [onSpeakingChange, isProcessing]);

  // Fetch upcoming reminders and tasks
  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/assistant/upcoming`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUpcomingItems(response.data);
      } catch (error) {
        console.error('Failed to fetch upcoming items:', error);
      }
    };
    
    if (token) {
      fetchUpcoming();
    }
  }, [token]);

  // Auto-scroll to bottom of conversation
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  // Process command (text or voice)
  const processCommand = async (command) => {
    if (!command.trim()) return;
    
    setIsProcessing(true);
    setVisualizerState('thinking');
    
    // Add user message to conversation
    setConversation(prev => [...prev, {
      role: 'user',
      content: command,
      timestamp: new Date()
    }]);
    
    setInputText('');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/assistant/command`,
        { command, language: 'en' },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      const { intent, response: aiResponse, action_taken, suggestions: newSuggestions } = response.data;
      
      // Add AI response to conversation
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: aiResponse,
        intent,
        action_taken,
        timestamp: new Date()
      }]);
      
      if (newSuggestions) {
        setSuggestions(newSuggestions);
      }
      
      // Refresh upcoming items if action was taken
      if (action_taken) {
        const upcomingResponse = await axios.get(`${API_URL}/api/assistant/upcoming`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUpcomingItems(upcomingResponse.data);
      }
      
      // Speak the response if voice is enabled
      if (voiceEnabled) {
        try {
          await speak(aiResponse);
        } catch (error) {
          console.log('Voice not available:', error);
        }
      }
      
      setVisualizerState('idle');
      
    } catch (error) {
      toast.error('Failed to process command');
      console.error(error);
      setVisualizerState('idle');
    } finally {
      setIsProcessing(false);
    }
  };

  // Start voice recording using Web Speech API
  const startRecording = useCallback(async () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    try {
      recognitionRef.current.onstart = () => {
        setVisualizerState('listening');
        if (visualizerRef.current) {
          visualizerRef.current.startListening();
        }
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          processCommand(finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          toast.error('No speech detected. Please try again.');
        } else if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone access.');
        }
        setVisualizerState('idle');
      };

      recognitionRef.current.onend = () => {
        if (visualizerState === 'listening') {
          setVisualizerState('idle');
        }
        if (visualizerRef.current) {
          visualizerRef.current.stopListening();
        }
      };

      recognitionRef.current.start();
    } catch (error) {
      console.error('Start recording error:', error);
      toast.error('Could not start voice recording');
      setVisualizerState('idle');
    }
  }, [processCommand, visualizerState]);

  // Stop voice recording
  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (visualizerRef.current) {
      visualizerRef.current.stopListening();
    }
  }, []);

  // Handle visualizer click
  const handleVisualizerClick = () => {
    if (isProcessing) return;
    
    if (visualizerState === 'listening') {
      stopRecording();
    } else if (visualizerState === 'speaking') {
      stopSpeaking();
      setVisualizerState('idle');
    } else if (visualizerState === 'idle') {
      startRecording();
    }
  };

  // Handle text submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputText.trim()) {
      processCommand(inputText);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    processCommand(suggestion);
  };

  const userName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="max-w-6xl mx-auto" data-testid="assistant-page">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Assistant Interface */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="font-heading text-4xl font-bold mb-2 text-[var(--text-primary)]">
              Personal Assistant
            </h1>
            <p className="text-[var(--text-secondary)]">
              Hi {userName}! I'm AIRA, your AI brain. How can I help you today?
            </p>
          </div>

          {/* AI Brain Visualizer - Central Interface */}
          <GlassCard className="p-8">
            <div className="flex flex-col items-center">
              <motion.div
                whileHover={{ scale: isProcessing ? 1 : 1.02 }}
                whileTap={{ scale: isProcessing ? 1 : 0.98 }}
                onClick={handleVisualizerClick}
                className={`cursor-pointer ${isProcessing && visualizerState === 'thinking' ? 'pointer-events-none' : ''}`}
                data-testid="assistant-visualizer"
              >
                <AIBrainVisualizer
                  ref={visualizerRef}
                  mode="interactive"
                  size="lg"
                  state={visualizerState}
                  isActive={true}
                  showLabel={false}
                />
              </motion.div>

              {/* Voice Controls */}
              <div className="flex items-center gap-4 mt-6">
                <button
                  onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                  className={`p-3 rounded-full transition-all ${
                    voiceEnabled 
                      ? 'bg-[var(--color-primary)] text-white' 
                      : 'bg-[var(--bg-card)] text-[var(--text-secondary)]'
                  }`}
                  title="Voice settings"
                >
                  {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                </button>
                
                {visualizerState === 'listening' && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={stopRecording}
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-full"
                  >
                    <MicOff className="w-4 h-4" />
                    Stop Recording
                  </motion.button>
                )}

                {visualizerState === 'speaking' && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => { stopSpeaking(); setVisualizerState('idle'); }}
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-full"
                  >
                    <VolumeX className="w-4 h-4" />
                    Stop Speaking
                  </motion.button>
                )}
              </div>

              {/* Voice Settings Modal */}
              <AnimatePresence>
                {showVoiceSettings && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="mt-4 p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)]"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-[var(--text-primary)]">Voice Settings</h4>
                      <button
                        onClick={() => setShowVoiceSettings(false)}
                        className="p-1 hover:bg-[var(--bg-card-hover)] rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <VoiceSettingsPanel variant="compact" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Status Text */}
              <p className="mt-4 text-[var(--text-tertiary)] text-sm">
                {visualizerState === 'idle' && 'Click the AI brain or type below to interact'}
                {visualizerState === 'listening' && 'Listening... Speak naturally'}
                {visualizerState === 'thinking' && 'Processing your request...'}
                {visualizerState === 'speaking' && 'AIRA is speaking...'}
              </p>
            </div>
          </GlassCard>

          {/* Text Input */}
          <form onSubmit={handleSubmit} className="relative">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Type a command or ask a question..."
              className="input-field w-full pr-24 py-4 text-lg"
              disabled={isProcessing}
              data-testid="assistant-input"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <button
                type="button"
                onClick={startRecording}
                disabled={isProcessing || visualizerState === 'listening'}
                className="p-2 hover:bg-[var(--bg-card-hover)] rounded-lg transition-colors"
                title="Voice input"
              >
                <Mic className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
              <button
                type="submit"
                disabled={isProcessing || !inputText.trim()}
                className="p-2 bg-[var(--color-primary)] text-white rounded-lg disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>

          {/* Quick Suggestions */}
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, idx) => (
              <motion.button
                key={idx}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isProcessing}
                className="px-4 py-2 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-full text-sm text-[var(--text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all disabled:opacity-50"
              >
                <Sparkles className="w-3 h-3 inline mr-2" />
                {suggestion}
              </motion.button>
            ))}
          </div>

          {/* Conversation History */}
          {conversation.length > 0 && (
            <GlassCard className="p-6 max-h-[400px] overflow-y-auto custom-scrollbar">
              <h3 className="font-heading text-lg font-semibold mb-4 text-[var(--text-primary)]">
                Conversation
              </h3>
              <div className="space-y-4">
                <AnimatePresence>
                  {conversation.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={msg.role === 'user' ? 'chat-message-user' : 'chat-message-ai'}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.action_taken && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <span className="text-xs text-[var(--color-success)] flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Action completed
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                <div ref={conversationEndRef} />
              </div>
            </GlassCard>
          )}
        </div>

        {/* Sidebar - Quick Actions & Upcoming */}
        <div className="space-y-6">
          {/* Upcoming Reminders */}
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold flex items-center gap-2 text-[var(--text-primary)]">
                <Bell className="w-5 h-5 text-[var(--color-accent)]" />
                Upcoming Reminders
              </h3>
            </div>
            
            {upcomingItems.reminders?.length > 0 ? (
              <div className="space-y-3">
                {upcomingItems.reminders.map((reminder, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] hover:border-[var(--color-accent)]/50 transition-colors"
                  >
                    <p className="font-medium text-sm text-[var(--text-primary)]">{reminder.title}</p>
                    <p className="text-xs text-[var(--text-tertiary)] flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {new Date(reminder.remind_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--text-tertiary)] text-sm text-center py-4">
                No upcoming reminders
              </p>
            )}
            
            <button
              onClick={() => processCommand("Set a reminder")}
              className="w-full mt-4 py-2 text-sm text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors"
            >
              + Add Reminder
            </button>
          </GlassCard>

          {/* Today's Tasks */}
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold flex items-center gap-2 text-[var(--text-primary)]">
                <ListTodo className="w-5 h-5 text-[var(--color-primary)]" />
                Your Tasks
              </h3>
            </div>
            
            {upcomingItems.tasks?.length > 0 ? (
              <div className="space-y-3">
                {upcomingItems.tasks.map((task, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] hover:border-[var(--color-primary)]/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        task.priority === 'high' ? 'bg-red-500' :
                        task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                      }`} />
                      <div>
                        <p className="font-medium text-sm text-[var(--text-primary)]">{task.title}</p>
                        {task.due_date && (
                          <p className="text-xs text-[var(--text-tertiary)] mt-1">
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--text-tertiary)] text-sm text-center py-4">
                No pending tasks
              </p>
            )}
            
            <button
              onClick={() => processCommand("Add a new task")}
              className="w-full mt-4 py-2 text-sm text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors"
            >
              + Add Task
            </button>
          </GlassCard>

          {/* Quick Help */}
          <GlassCard className="p-6">
            <h3 className="font-heading font-semibold flex items-center gap-2 mb-4 text-[var(--text-primary)]">
              <HelpCircle className="w-5 h-5 text-[var(--color-secondary)]" />
              What I Can Do
            </h3>
            <div className="space-y-2 text-sm text-[var(--text-secondary)]">
              <p className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-[var(--color-accent)]" />
                Set reminders & manage tasks
              </p>
              <p className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-[var(--color-accent)]" />
                Answer questions & search info
              </p>
              <p className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-[var(--color-accent)]" />
                Plan your daily schedule
              </p>
              <p className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-[var(--color-accent)]" />
                Voice & text interaction
              </p>
            </div>
          </GlassCard>
        </div>
      </div>
      
      {/* News Section - Compact view for dashboard */}
      <div className="max-w-4xl mx-auto mt-8">
        <NewsSection compact={true} />
      </div>
    </div>
  );
}
